import { CONTINUOUS_LOAD_HEADROOM } from "./wire-tables";

/** Signed in each cable's stored from→to orientation. Peak is the complete
 * peak-current contribution, not an increment on sustained or starting. */
export type SignedDutyCurrent = { sustainedA: number; startingA: number; peakA: number };
export type CurrentAlternative = { id: string; byCable: ReadonlyMap<string, SignedDutyCurrent> };
/** Alternatives are exclusive (source route or device mode). Different groups
 * may operate together. Include an empty alternative when off is permitted. */
export type CurrentChoiceGroup = { id: string; alternatives: readonly CurrentAlternative[] };
export type CableCurrentEnvelope = {
  continuous: number;
  peak: number;
  requiredAmpacity: number;
  signedMinimumA: number;
  signedMaximumA: number;
  startingCurrent: number;
  sustainedCurrent: number;
};
const ZERO: SignedDutyCurrent = { sustainedA: 0, startingA: 0, peakA: 0 };

export function sumCurrentCase(alternatives: readonly CurrentAlternative[]): Map<string, SignedDutyCurrent> {
  const out = new Map<string, SignedDutyCurrent>();
  for (const alternative of alternatives) for (const [id, value] of alternative.byCable) {
    const current = out.get(id) ?? ZERO;
    out.set(id, {
      sustainedA: current.sustainedA + value.sustainedA,
      startingA: current.startingA + value.startingA,
      peakA: current.peakA + value.peakA,
    });
  }
  return out;
}

/** Maximize signed linear objectives over independent device/source choices.
 * This preserves cancellation and exclusive engine modes without enumerating
 * the Cartesian product of every component's operating states. The extrema
 * are design bounds, not a prediction of battery or parallel-cable sharing. */
export function currentEnvelope(groups: readonly CurrentChoiceGroup[], continuousLoadHeadroom = CONTINUOUS_LOAD_HEADROOM): Map<string, CableCurrentEnvelope> {
  const ids = new Set(groups.flatMap(group => group.alternatives.flatMap(choice => [...choice.byCable.keys()])));
  const out = new Map<string, CableCurrentEnvelope>();
  for (const id of ids) {
    const support = (objective: (value: SignedDutyCurrent) => number): number => groups.reduce((sum, group) =>
      sum + (group.alternatives.length ? Math.max(...group.alternatives.map(choice => objective(choice.byCable.get(id) ?? ZERO))) : 0), 0);
    const signedMaximumA = support(c => c.sustainedA + c.startingA);
    const signedMinimumA = -support(c => -c.sustainedA - c.startingA);
    const headroom = continuousLoadHeadroom - 1;
    // |sustained + starting| + 0.25|sustained| never falls below actual
    // conductor current, even when the transient opposes a sustained load.
    // Taking four linear supports maximizes this duty policy over real choices.
    const requiredAmpacity = Math.max(...[-1, 1].flatMap(totalSign => [-1, 1].map(sustainedSign =>
      support(c => totalSign * (c.sustainedA + c.startingA) + sustainedSign * headroom * c.sustainedA))));
    out.set(id, {
      continuous: Math.max(Math.abs(signedMinimumA), Math.abs(signedMaximumA)),
      // A device may remain at normal draw while another reaches its peak.
      // This matters on shared converter returns, where internal opposing
      // flows can make that device's net normal current exceed its net peak.
      peak: Math.max(
        support(c => Math.max(c.peakA, c.sustainedA + c.startingA)),
        support(c => Math.max(-c.peakA, -c.sustainedA - c.startingA)),
      ),
      requiredAmpacity,
      signedMinimumA,
      signedMaximumA,
      startingCurrent: Math.max(support(c => c.startingA), support(c => -c.startingA)),
      sustainedCurrent: Math.max(support(c => c.sustainedA), support(c => -c.sustainedA)),
    });
  }
  return out;
}
